import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { FloatingWhatsApp } from './FloatingWhatsApp';
import { Calendar, Clock, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { useCanonical } from '../hooks/useCanonical';

const articles = [
  {
    slug: 'retour-affectif-rapide-geneve',
    title: 'Retour Affectif Rapide à Genève : Comment Faire Revenir l\'Être Aimé',
    excerpt: 'Découvrez les rituels ancestraux pour un retour affectif rapide à Genève. Comment fonctionne le retour de l\'être aimé ? Quels résultats attendre en 3-7 jours ?',
    image: 'https://images.unsplash.com/photo-1654619139072-682d58ba1b93?w=400&q=60',
    date: '28 janvier 2026',
    readTime: '8 min',
    category: 'Retour Affectif',
  },
  {
    slug: 'voyant-medium-serieux-geneve',
    title: 'Comment Trouver un Voyant Médium Sérieux à Genève ?',
    excerpt: 'Les critères pour reconnaître un vrai voyant médium sérieux à Genève. Évitez les charlatans et trouvez un professionnel de confiance pour votre consultation.',
    image: 'https://images.unsplash.com/photo-1588639244296-189718bc8c2b?w=400&q=60',
    date: '25 janvier 2026',
    readTime: '6 min',
    category: 'Voyance',
  },
  {
    slug: 'desenvoutement-signes-envoutement',
    title: 'Les 10 Signes d\'un Envoûtement et Comment s\'en Libérer',
    excerpt: 'Vous pensez être victime d\'un envoûtement ? Découvrez les signes qui ne trompent pas et les solutions de désenvoûtement puissant proposées par notre marabout à Genève.',
    image: 'https://images.unsplash.com/photo-1696159197197-6395daa1dda3?w=400&q=60',
    date: '22 janvier 2026',
    readTime: '7 min',
    category: 'Désenvoûtement',
  },
  {
    slug: 'rituels-protection-spirituelle',
    title: 'Protection Spirituelle : Rituels Ancestraux pour se Protéger',
    excerpt: 'Quels rituels de protection spirituelle utiliser contre le mauvais oeil et la jalousie ? Notre marabout africain à Genève vous explique les méthodes ancestrales.',
    image: 'https://images.unsplash.com/photo-1671619394122-98483dc50616?w=400&q=60',
    date: '18 janvier 2026',
    readTime: '5 min',
    category: 'Protection',
  },
  {
    slug: 'marabout-africain-traditions-ancestrales',
    title: 'L\'Art du Marabout Africain : Traditions et Rituels Ancestraux',
    excerpt: 'Plongez dans l\'univers du marabout africain. Origines, traditions, rituels : tout savoir sur cette pratique spirituelle millénaire venue d\'Afrique de l\'Ouest.',
    image: 'https://images.unsplash.com/photo-1748633412469-a045f8015d00?w=400&q=60',
    date: '15 janvier 2026',
    readTime: '9 min',
    category: 'Spiritualité',
  },
  {
    slug: 'chance-aux-jeux-rituels-fortune',
    title: 'Rituels de Chance aux Jeux : Attirer la Fortune et la Prospérité',
    excerpt: 'Les rituels de chance ancestraux pour attirer la fortune. Comment un marabout à Genève peut transformer votre situation financière grâce aux rituels de prospérité.',
    image: 'https://images.unsplash.com/photo-1704628814123-05fbf38955df?w=400&q=60',
    date: '12 janvier 2026',
    readTime: '6 min',
    category: 'Chance',
  },
  {
    slug: 'guerison-spirituelle-africaine',
    title: 'Guérison Spirituelle Africaine : Soins Énergétiques Ancestraux',
    excerpt: 'La guérison spirituelle africaine : une approche holistique des maux du corps et de l\'esprit. Découvrez les techniques ancestrales de purification et de guérison.',
    image: 'https://images.unsplash.com/photo-1572379760403-530fb30b9044?w=400&q=60',
    date: '8 janvier 2026',
    readTime: '7 min',
    category: 'Guérison',
  },
  {
    slug: 'reconciliation-familiale-marabout',
    title: 'Réconciliation Familiale : Le Rôle du Marabout dans l\'Harmonie',
    excerpt: 'Comment un marabout africain peut résoudre les conflits familiaux et restaurer l\'harmonie au sein du foyer. Les rituels de réconciliation à Genève.',
    image: 'https://images.unsplash.com/photo-1712226209738-cc5f4240433f?w=400&q=60',
    date: '5 janvier 2026',
    readTime: '5 min',
    category: 'Famille',
  },
];

export { articles };

export const BlogList = () => {
  useCanonical('/blog');
  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Blog Marabout Genève | Voyance, Retour Affectif, Spiritualité';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Blog du marabout à Genève. Articles sur le retour affectif, la voyance, le désenvoûtement, la protection spirituelle. Conseils et rituels ancestraux.');
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Header />

      {/* Hero */}
      <section className="pt-20 pb-10 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgb(212, 168, 67) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/" className="text-amber-400 hover:text-amber-300 text-sm mb-4 inline-block">← Retour à l'accueil</Link>
          <span className="block text-amber-500 text-sm font-semibold tracking-widest uppercase mb-3">Blog Spiritualité & Voyance</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}} data-testid="blog-title">
            <span className="text-amber-400">Blog</span> Marabout Genève
          </h1>
          <p className="text-amber-100/60 max-w-2xl text-lg">
            Articles, conseils et rituels ancestraux par notre grand marabout africain à Genève. Retour affectif, voyance, désenvoûtement, protection spirituelle.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="pb-14 bg-neutral-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="group bg-gradient-to-b from-neutral-900 to-neutral-950 border border-amber-500/20 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all duration-500 hover:-translate-y-1"
                data-testid={`article-card-${article.slug}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <span className="absolute bottom-3 left-3 text-xs text-amber-300 bg-amber-500/20 border border-amber-500/30 px-3 py-1 rounded-full">
                    {article.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center space-x-4 text-xs text-amber-200/40 mb-3">
                    <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{article.date}</span>
                    <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{article.readTime}</span>
                  </div>
                  <h2 className="text-white font-semibold text-base mb-2 group-hover:text-amber-400 transition-colors leading-snug">
                    {article.title}
                  </h2>
                  <p className="text-amber-100/50 text-sm leading-relaxed mb-3">{article.excerpt}</p>
                  <span className="inline-flex items-center text-amber-400 text-sm font-medium">
                    Lire l'article <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-br from-amber-500/5 via-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Besoin d'une <span className="text-amber-400">Consultation</span> ?
            </h2>
            <p className="text-amber-100/60 mb-6">Contactez notre grand marabout africain à Genève. Consultation gratuite, résultats en 3-7 jours.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+41767836186" className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-6 py-3 rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all">
                <Phone className="w-5 h-5 mr-2" />+41 76 783 61 86
              </a>
              <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-lg transition-all">
                <MessageCircle className="w-5 h-5 mr-2" />WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};
